<script setup lang="ts">
import { ref } from 'vue';
import InputText from 'primevue/inputtext';
import MultiFormSelector from './MultiFormSelector.vue';
import Select from 'primevue/select';
import Checkbox from 'primevue/checkbox';
import { UserModel } from '@/models/user';
import { FormModel } from '@/models/form';
import { ButtonType, CustomButton } from '@/components';
import { validateEmail } from '@/utils';
import { useStudyStore } from '@/stores/study';

const props = defineProps<{
  invite: UserModel;
}>();

const studyStore = useStudyStore();

const localInvite = ref<UserModel>({ ...props.invite });

const viewEntriesPermissions = ['View all entries', 'View own entries'];
const selectedViewEntryPermission = ref(
  props.invite.permissions.viewEntries === true ?
    viewEntriesPermissions[0] :
    viewEntriesPermissions[1]
);

const pumpNotificationProfiles = ['Surgeon', 'Perfusionist'];
const selectedNotificationProfile = ref(
  props.invite.permissions.notificationProfile
);

const emit = defineEmits(['onDelete', 'updateInvite', 'onEmailPaste']);

const onDeleteClick = () => {
  emit('onDelete', props.invite);
};

const onEmailChangeListener = () => {
  localInvite.value.invalidEmail = !validateEmail(localInvite.value.email);
  emit('updateInvite', localInvite.value);
};

const onViewFormsChangeListner = (updatedForms: FormModel[]) => {
  if (updatedForms.length === localInvite.value.formsPermissions?.length) {
    localInvite.value.permissions.forms = true;
  } else {
    localInvite.value.permissions.forms = updatedForms.map(
      form => form.base64Id
    ) as string[];
  }
};

const onEditFormsChangeListner = (updatedForms: FormModel[]) => {
  if (updatedForms.length === localInvite.value.editFormsPermissions?.length) {
    localInvite.value.permissions.editForms = true;
  } else {
    localInvite.value.permissions.editForms = updatedForms.map(
      form => form.base64Id
    ) as string[];
  }
};

const onViewEntriesChangeListener = () => {
  localInvite.value.permissions.viewEntries =
    selectedViewEntryPermission.value === viewEntriesPermissions[0];
};

const onNotificationProfileChangeListener = () => {
  localInvite.value.permissions.notificationProfile =
    selectedNotificationProfile.value;
};

const onPaste = (event: ClipboardEvent) => {
  event.preventDefault();
  const pastedData = event.clipboardData?.getData('text/plain');
  emit('onEmailPaste', pastedData, props.invite);
};
</script>

<template>
  <div class="row-root">
    <div class="permissions">
      <div class="form-permissions">
        <div class="email">
          <div class="select-header">User email</div>
          <InputText type="text"
            v-model="localInvite.email"
            placeholder="Enter email"
            @paste="onPaste"
            @input="onEmailChangeListener"
            :invalid="localInvite.invalidEmail"/>
          <div class="email-error">
            <span v-if="localInvite.invalidEmail"
severity="error">Invalid email address</span>
          </div>
        </div>
        <div class="view-form">
          <div class="select-header">View forms</div>
          <MultiFormSelector :selectedFormsIds="localInvite.formsPermissions as string[]"
            @onFormChangeListener="onViewFormsChangeListner"/>
        </div>
        <div class="edit-form">
          <div class="select-header">Add entries</div>
          <div>
            <MultiFormSelector :selectedFormsIds="localInvite.editFormsPermissions as string[]"
              @onFormChangeListener="onEditFormsChangeListner"/>
          </div>
        </div>
        <div class="view-entries">
          <div class="select-header">View entries</div>
          <div>
            <Select v-model="selectedViewEntryPermission"
              @change="onViewEntriesChangeListener"
              placeholder="Select"
              :options="viewEntriesPermissions"/>
          </div>
        </div>
        <div class="user-type"
          v-if="
            studyStore.selectedStudy?.flags?.customDataDictionary === 'PUMP'
          ">
          <div class="select-header">Notification profile</div>
          <div>
            <Select v-model="selectedNotificationProfile"
              @change="onNotificationProfileChangeListener"
              placeholder="Select"
              :options="pumpNotificationProfiles"/>
          </div>
        </div>
      </div>

      <div class="other-permissions">
        <div class="checkbox-permissions">
          <label for="permission" class="permission-label"> Edit cases </label>
          <Checkbox v-model="localInvite.permissions.editCases"
            :binary="true"/>
        </div>
        <div class="checkbox-permissions">
          <label for="permission" class="permission-label"> Export data </label>
          <Checkbox v-model="localInvite.permissions.export" :binary="true" />
        </div>
        <div class="checkbox-permissions">
          <label for="permission" class="permission-label">
            Create study
          </label>
          <Checkbox v-model="localInvite.permissions.createStudy"
            :binary="true"/>
        </div>
        <div class="checkbox-permissions">
          <label for="permission" class="permission-label">
            Manage users
          </label>
          <Checkbox v-model="localInvite.permissions.manageInvest"
            :binary="true"/>
        </div>
      </div>

      <hr />
    </div>
    <div class="button">
      <CustomButton text=""
        v-if="localInvite.showDelete"
        :type="ButtonType.NEGATIVE"
        :icon="{ icon: 'trash', style: 'fas' }"
        @onClick="onDeleteClick"/>
    </div>
  </div>
</template>

<style scoped lang="scss">
.row-root {
  display: flex;
  padding: 16px 0;
}
.form-permissions {
  display: flex;
  gap: 16px;
}
.select-header {
  font-weight: 600;
  margin-bottom: 8px;
}
.p-select {
  width: 250px;
}
.other-permissions {
  display: flex;
  margin-top: 32px;
  gap: 32px;
}
.checkbox-permissions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}
.permission-checkbox {
  margin-top: 8px;
}
.permission-label {
  font-weight: 600;
}
hr {
  border-color: hsla(0, 0%, 0%, 0.1);
  margin-top: 32px;
}
.button {
  display: flex;
  flex-direction: column;
  padding-left: 16px;
}
.checkbox {
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.email-error {
  display: flex;
  justify-content: flex-start;
  span {
    color: red;
  }
}
</style>
